/**
 * yearDebriefBuilder — 25/07/2026 (demande user prod).
 *
 * « Je souhaiterais pour l'apprenant et son responsable pédagogique un
 * débrief chronologique en fin d'année avec les résultats et points de
 * passage au cours de l'année. »
 *
 * Construit, à la clôture d'exercice, la frise chronologique de l'année :
 * résultats mensuels + points de passage datés (AO gagnés/perdus,
 * événements marquants, ouvertures de dépôts, achats de véhicules,
 * recrutements). Attaché au rapport annuel (`pendingAnnualReport.yearDebrief`)
 * et affiché dans l'onglet « Débrief de l'année » du Conseil, ainsi que
 * dans la vue superviseur (via la télémétrie /telemetry/year-debrief).
 */

const CHECKPOINT_CAP_PER_KIND = 6;

export function buildYearDebrief({ state, closingYear, yearMonths, finalTreasury, finalVehicles, driversAfterLease }) {
  const dayStart = (closingYear - 1) * 360;
  const dayEnd = closingYear * 360; // exclusif
  const inYear = d => typeof d === 'number' && d >= dayStart && d < dayEnd;
  const checkpoints = [];

  (state.tenderHistory || []).forEach(t => {
    if (!inYear(t.resolvedOnDay)) return;
    checkpoints.push({
      day: t.resolvedOnDay,
      type: t.outcome === 'won' ? 'ao_won' : 'ao_lost',
      label: t.outcome === 'won'
        ? `AO remporté — ${t.clientName || 'client'}`
        : `AO perdu — ${t.clientName || 'client'}`,
    });
  });

  // Événements marquants : top impact cash absolu, plafonné à 8.
  (state.eventLog || [])
    .filter(e => inYear(e.resolvedAt && e.resolvedAt.totalDays))
    .sort((a, b) => Math.abs(b.netCashImpact || 0) - Math.abs(a.netCashImpact || 0))
    .slice(0, 8)
    .forEach(e => checkpoints.push({
      day: e.resolvedAt.totalDays,
      type: 'event',
      label: e.name || e.title || 'Événement',
      impact: Math.round(e.netCashImpact || 0),
    }));

  (state.ownedDepots || []).forEach(d => {
    if (d.isHeadquarters || !inYear(d.openedDay)) return;
    checkpoints.push({ day: d.openedDay, type: 'depot', label: `Ouverture ${d.name || 'dépôt'}` });
  });

  const bought = (finalVehicles || state.vehicles || [])
    .filter(v => !v.isDemo && inYear(v.purchaseDay))
    .sort((a, b) => (a.purchaseDay || 0) - (b.purchaseDay || 0));
  bought.slice(0, CHECKPOINT_CAP_PER_KIND).forEach(v => checkpoints.push({
    day: v.purchaseDay, type: 'fleet',
    label: `Nouveau véhicule — ${[v.brand, v.model].filter(Boolean).join(' ') || 'camion'}`,
  }));
  if (bought.length > CHECKPOINT_CAP_PER_KIND) {
    checkpoints.push({
      day: bought[CHECKPOINT_CAP_PER_KIND].purchaseDay, type: 'fleet',
      label: `+${bought.length - CHECKPOINT_CAP_PER_KIND} autres véhicules acquis`,
    });
  }

  const hired = (driversAfterLease || state.drivers || [])
    .filter(d => inYear(d.hiredAt))
    .sort((a, b) => (a.hiredAt || 0) - (b.hiredAt || 0));
  hired.slice(0, CHECKPOINT_CAP_PER_KIND).forEach(d => checkpoints.push({
    day: d.hiredAt, type: 'hr', label: `Recrutement — ${d.name || 'chauffeur'}`,
  }));
  if (hired.length > CHECKPOINT_CAP_PER_KIND) {
    checkpoints.push({
      day: hired[CHECKPOINT_CAP_PER_KIND].hiredAt, type: 'hr',
      label: `+${hired.length - CHECKPOINT_CAP_PER_KIND} autres recrutements`,
    });
  }

  checkpoints.sort((a, b) => (a.day || 0) - (b.day || 0));
  const withMonth = checkpoints.map(c => ({
    ...c,
    monthIndex: Math.min(12, Math.max(1, Math.floor(((c.day || 0) - dayStart) / 30) + 1)),
  }));

  const months = (yearMonths || []).map(m => ({
    month: m.month,
    revenue: Math.round(m.totalRevenue || 0),
    netResult: Math.round(m.netResult ?? ((m.totalRevenue || 0) - (m.totalExpenses || 0))),
    treasury: Math.round(m.treasury || 0),
    contractsCompleted: m.contractsCompleted || 0,
  }));

  const aoWon = withMonth.filter(c => c.type === 'ao_won').length;

  return {
    year: closingYear,
    checkpoints: withMonth,
    months,
    kpis: {
      totalRevenue: months.reduce((s, m) => s + (m.revenue || 0), 0),
      netResult: months.reduce((s, m) => s + (m.netResult || 0), 0),
      treasuryEnd: Math.round(finalTreasury ?? state.treasury ?? 0),
      fleetEnd: (finalVehicles || state.vehicles || []).length,
      driversEnd: (driversAfterLease || state.drivers || []).length,
      reputation: Math.round(state.reputation || 0),
      notoriety: Math.round(state.notoriety || 0),
      phase: state.phase || 1,
      aoWon,
      contractsCompleted: months.reduce((s, m) => s + (m.contractsCompleted || 0), 0),
      playTimeSeconds: Math.round(state.playTimeSeconds || 0),
      totalDaysEnd: (state.totalDays || 0) + 1,
    },
  };
}
